import { jsxs, jsx } from "react/jsx-runtime";
import { css, cx } from "@/styled-system/css";
import { Text, Button } from "@zillow/constellation-10";
import withPreload from "../../utils/with-preload.esm.js";
import PostReadTime from "../PostReadTime/PostReadTime.esm.js";
import Source from "../Source/Source.esm.js";
import { getPostCardImage, getPostCardImageRadius } from "./image-util.esm.js";
const SourcePreload = withPreload(Source);
const ImgElement = (props) => (
  // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
  /* @__PURE__ */ jsx("img", { ...props })
);
const PreloadImg = withPreload(ImgElement);
const overlayLinkStyles = css({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
});
const imgBaseStyles = css({
  aspectRatio: "16 / 9"
});
const pictureStyles = css({
  display: "block",
  width: "100%",
  height: "100%"
});
const imgRadiusStyles = css({
  borderRadius: "obj.default"
});
const badgeLabelStyles = css({
  display: "flex",
  marginBottom: "tighter"
});
const withShadowClass = css({
  boxShadow: "lg",
  backgroundColor: "bg.neutral"
});
const noShadowClass = css({
  boxShadow: "none",
  backgroundColor: "transparent"
});
const fallbackContainerClass = css({
  display: "grid",
  position: "relative",
  gridTemplateColumns: "repeat(12, 1fr)",
  borderRadius: "obj.default",
  columnGap: "loose",
  textDecoration: "none",
  overflow: "hidden"
});
const fallbackBodyClass = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start"
});
const verticalContainerClass = css({
  display: "grid",
  position: "relative",
  gridTemplateColumns: "repeat(12, 1fr)",
  borderRadius: "obj.default",
  textDecoration: "none",
  overflow: "hidden",
  columnGap: 0,
  gridTemplateRows: "auto 1fr",
  height: "100%"
});
const verticalBodySmMd = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gridColumnEnd: "span 12",
  padding: {
    base: "default",
    md: "loose"
  },
  justifyContent: "start"
});
const verticalBodyLgXl = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gridColumnEnd: "span 12",
  padding: {
    base: "default",
    md: "looser"
  },
  justifyContent: "start"
});
const verticalImageClass = css({
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  gridColumnEnd: "span 12"
});
const horizontalMdContainerClass = css({
  display: "grid",
  position: "relative",
  gridTemplateColumns: "repeat(12, 1fr)",
  borderRadius: "obj.default",
  textDecoration: "none",
  overflow: "hidden",
  columnGap: {
    base: 0,
    md: "loose"
  },
  gridTemplateRows: {
    base: "auto 1fr",
    md: "none"
  },
  height: {
    base: "100%",
    md: "auto"
  }
});
const horizontalMdBodyClass = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gridColumnEnd: {
    base: "span 12",
    md: "span 6"
  },
  paddingTop: {
    base: "default",
    md: "loose"
  },
  paddingRight: {
    base: "default",
    md: "loose"
  },
  paddingBottom: {
    base: "default",
    md: "loose"
  },
  paddingLeft: {
    base: "default",
    md: 0
  }
});
const horizontalMdImageClass = css({
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  gridColumnEnd: {
    base: "span 12",
    md: "span 6"
  }
});
const horizontalLgContainerClass = css({
  display: "grid",
  position: "relative",
  gridTemplateColumns: "repeat(12, 1fr)",
  borderRadius: "obj.default",
  textDecoration: "none",
  overflow: "hidden",
  columnGap: {
    base: 0,
    md: "layout.vast.default"
  },
  gridTemplateRows: {
    base: "auto 1fr",
    md: "none"
  },
  height: {
    base: "100%",
    md: "auto"
  }
});
const horizontalLgBodyClass = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gridColumnEnd: {
    base: "span 12",
    md: "span 6"
  },
  paddingTop: {
    base: "default",
    md: "looser"
  },
  paddingRight: {
    base: "default",
    md: "looser"
  },
  paddingBottom: {
    base: "default",
    md: "looser"
  },
  paddingLeft: {
    base: "default",
    md: 0
  }
});
const horizontalLgImageClass = css({
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  gridColumnEnd: {
    base: "span 12",
    md: "span 6"
  }
});
const horizontalXlContainerClass = css({
  display: "grid",
  position: "relative",
  gridTemplateColumns: "repeat(12, 1fr)",
  borderRadius: "obj.default",
  textDecoration: "none",
  overflow: "hidden",
  columnGap: {
    base: 0,
    md: "loose"
  },
  gridTemplateRows: {
    base: "auto 1fr",
    md: "none"
  },
  height: {
    base: "100%",
    md: "auto"
  }
});
const horizontalXlBodyClass = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gridColumn: {
    base: "span 12",
    md: "span 5"
  },
  paddingTop: {
    base: "default",
    md: "looser"
  },
  paddingRight: {
    base: "default",
    md: 0
  },
  paddingBottom: {
    base: "default",
    md: "looser"
  },
  paddingLeft: {
    base: "default",
    md: "looser"
  }
});
const horizontalXlImageClass = css({
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  gridColumnEnd: {
    base: "span 12",
    md: "span 7"
  }
});
const styles = {
  vertical: {
    sm: {
      headingTextStyle: "body-bold",
      containerClass: verticalContainerClass,
      bodyClass: verticalBodySmMd,
      imageClass: verticalImageClass
    },
    md: {
      headingTextStyle: "heading-xs",
      containerClass: verticalContainerClass,
      bodyClass: verticalBodySmMd,
      imageClass: verticalImageClass
    },
    lg: {
      headingTextStyle: "heading-sm",
      headingCss: {
        fontSize: {
          base: "body-lg",
          md: "heading-sm"
        },
        lineHeight: {
          base: "body-lg",
          md: "heading-sm"
        }
      },
      excerptTextStyle: "body",
      excerptColor: "text.subtle",
      containerClass: verticalContainerClass,
      bodyClass: verticalBodyLgXl,
      imageClass: verticalImageClass
    },
    xl: {
      headingTextStyle: "heading-md",
      headingCss: {
        fontSize: {
          base: "body",
          xl: "heading-md"
        },
        lineHeight: {
          base: "body",
          xl: "heading-md"
        }
      },
      excerptTextStyle: "body",
      excerptColor: "text.subtle",
      containerClass: verticalContainerClass,
      bodyClass: verticalBodyLgXl,
      imageClass: verticalImageClass
    }
  },
  horizontal: {
    sm: {},
    md: {
      headingTextStyle: "body-bold",
      containerClass: horizontalMdContainerClass,
      bodyClass: horizontalMdBodyClass,
      imageClass: horizontalMdImageClass
    },
    lg: {
      headingTextStyle: "heading-sm",
      headingCss: {
        fontSize: {
          base: "body-lg",
          md: "heading-sm"
        },
        lineHeight: {
          base: "body-lg",
          md: "heading-sm"
        }
      },
      excerptTextStyle: "body",
      excerptColor: "text.subtle",
      containerClass: horizontalLgContainerClass,
      bodyClass: horizontalLgBodyClass,
      imageClass: horizontalLgImageClass
    },
    xl: {
      headingTextStyle: "heading-md",
      headingCss: {
        fontSize: {
          base: "body",
          xl: "heading-md"
        },
        lineHeight: {
          base: "body",
          xl: "heading-md"
        }
      },
      excerptTextStyle: "body",
      excerptColor: "text.subtle",
      containerClass: horizontalXlContainerClass,
      bodyClass: horizontalXlBodyClass,
      imageClass: horizontalXlImageClass
    }
  }
};
const defaultImage = {
  sourceUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAlgAAAHCBAMAAADBXapXAAAAJ1BMVEX29vrR0dXa2t7T09f09Pju7vLW1trk5Ojx8fXh4eXp6e3l5enc3OB7H3z4AAAE2ElEQVR42uzasUocURTH4VMo7sbqMLBBYrOVabVLKklnt5Bgv0UgpEreIKTYOkLsA75AhDxgVld3Vta594ql3/cOl/n/DhMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwDONfweNRtPJj6DNeebXoMn+cWZ+C1pc5NLhaVC3yFtfgqrded7q/gQ113nndVBxlmtvg6LRPNeMrYqr3PAmKNjLnrFVtjPNJQ+xfWJt+hwMeHXTOR5ia+dsOQge9TMfUD2Vztk2mQUDndNTPcXOGeDEXOwcY6viYw56Hzywnz1jq2znIgsOPcSBzjG26p1T1P0N7n3PNWOr4kMueYhP7hxjq+IqBzgxbxlnk19BjKa55CE2Oc8BTsy1zlE95c5pdvDSf6xZZFl3dGxs3dmtdM7RbPN2M3nZP9ZcZ8nJv5tuVD0rZ1nQXZ6uwtGJuT8lD3g3i5WxsVXpnO6y/2DOnZgj9nLQyafoLYyt/+zcsW/TQBTH8TfYagnTc9REbRgwFapEJw+IRuqQCaTAECoBqrpQCSS6EVr2wJABlqCKDoghQQywVQgkxFbxj+E0je+efXexYWC432fu9JV7vty9RI7MSJsd+crEEfNDtmgckTTx/mTrcuJY2aUl74+Y37HZ9Y7hbz0/Yn7t/heUbnt+xLzPJtu9EhuyBvnG9hY0u8868o15I2rzCrEcy5VUSxBLiHquS1jE0t3qkNU9RizdjY7rcAKxdO/dc26IpdSPyKHGiKVE38mlj1jiNehyiRGL51bdrYIYsUhsGRyGjFhUstXyPmIROVoFD/QHC7GI7K3Ck7F2/I5YKWurO8lTfT+KWClLq+CTfpFaY8SaMrcKD8UV/QSxzhmnrboxc9M46Ob3sXI0MC1X8sq5zwbbv/ybaetRwQ6nWu7r/YMvBETBIU+NXQ/WwWeCVHicn5JZQiqLMOapes/6YLV/EqilPXXTduYXvSSY2U248GBNWKl/8O8NaLPDF1bMD9YGfghD+4QzNzZ9KmxjXZdbhpk1yiwn6k4fsiqqFZ9SZoj/wKLwjDPNwgFphO2C3F4pz/PTa5t4B9paNUk5m10nglKLWTOSn3Q28FiJVtbhxz7XsWO3tpJjtSG38RK0t5JfYRpiZS+s7cIWKT8IhJglv7+kugB+maeCv/8ZgjcfyTcsRFRSsPfbx3tD4VnJVLuxn5esuvqgZCpfb6R1a1TC3rq/1/cVl/fusc+zDportEj4wu/BkArLe/AVX3TKDMjpbez9yJHSIpfwBPNZrIyc24UEw2xU7vdluuuY/BOxVsgm+IYxyXMlNll3Y0YsGavheKwQKx+rY3usEGuOM1eNwyIJI5Yh1qp5ZhKxFFZOKedRwohlibVSXNkRS7Ae/YUxI1YOa57IUVzEcsWKxHgpYhWwbqRGJhFrUaxWtlwhlgkLvYsv0CFWiVhbs6UdscrEagymrRDLgqXHaSvEKhmrnjBiWXEeYiEWYi2AWBUgVgWIVQFiVYBYFSBWBYhVAWJVgFgVINb/dO0fEAAAAAAAAAD8YQ8OBAAAAACA/F8bQVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVaQ8OSAAAAAAE/X/dj1ABAAAAAAAAAAAAAACAlQB0Y5H7ngzE2AAAAABJRU5ErkJggg==",
  altText: "",
  mediaDetails: {
    sizes: {
      full: {
        width: 720,
        height: 405,
        sourceUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAlgAAAHCBAMAAADBXapXAAAAJ1BMVEX29vrR0dXa2t7T09f09Pju7vLW1trk5Ojx8fXh4eXp6e3l5enc3OB7H3z4AAAE2ElEQVR42uzasUocURTH4VMo7sbqMLBBYrOVabVLKklnt5Bgv0UgpEreIKTYOkLsA75AhDxgVld3Vta594ql3/cOl/n/DhMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwDONfweNRtPJj6DNeebXoMn+cWZ+C1pc5NLhaVC3yFtfgqrded7q/gQ113nndVBxlmtvg6LRPNeMrYqr3PAmKNjLnrFVtjPNJQ+xfWJt+hwMeHXTOR5ia+dsOQge9TMfUD2Vztk2mQUDndNTPcXOGeDEXOwcY6viYw56Hzywnz1jq2znIgsOPcSBzjG26p1T1P0N7n3PNWOr4kMueYhP7hxjq+IqBzgxbxlnk19BjKa55CE2Oc8BTsy1zlE95c5pdvDSf6xZZFl3dGxs3dmtdM7RbPN2M3nZP9ZcZ8nJv5tuVD0rZ1nQXZ6uwtGJuT8lD3g3i5WxsVXpnO6y/2DOnZgj9nLQyafoLYyt/+zcsW/TQBTH8TfYagnTc9REbRgwFapEJw+IRuqQCaTAECoBqrpQCSS6EVr2wJABlqCKDoghQQywVQgkxFbxj+E0je+efXexYWC432fu9JV7vty9RI7MSJsd+crEEfNDtmgckTTx/mTrcuJY2aUl74+Y37HZ9Y7hbz0/Yn7t/heUbnt+xLzPJtu9EhuyBvnG9hY0u8868o15I2rzCrEcy5VUSxBLiHquS1jE0t3qkNU9RizdjY7rcAKxdO/dc26IpdSPyKHGiKVE38mlj1jiNehyiRGL51bdrYIYsUhsGRyGjFhUstXyPmIROVoFD/QHC7GI7K3Ck7F2/I5YKWurO8lTfT+KWClLq+CTfpFaY8SaMrcKD8UV/QSxzhmnrboxc9M46Ob3sXI0MC1X8sq5zwbbv/ybaetRwQ6nWu7r/YMvBETBIU+NXQ/WwWeCVHicn5JZQiqLMOapes/6YLV/EqilPXXTduYXvSSY2U248GBNWKl/8O8NaLPDF1bMD9YGfghD+4QzNzZ9KmxjXZdbhpk1yiwn6k4fsiqqFZ9SZoj/wKLwjDPNwgFphO2C3F4pz/PTa5t4B9paNUk5m10nglKLWTOSn3Q28FiJVtbhxz7XsWO3tpJjtSG38RK0t5JfYRpiZS+s7cIWKT8IhJglv7+kugB+maeCv/8ZgjcfyTcsRFRSsPfbx3tD4VnJVLuxn5esuvqgZCpfb6R1a1TC3rq/1/cVl/fusc+zDportEj4wu/BkArLe/AVX3TKDMjpbez9yJHSIpfwBPNZrIyc24UEw2xU7vdluuuY/BOxVsgm+IYxyXMlNll3Y0YsGavheKwQKx+rY3usEGuOM1eNwyIJI5Yh1qp5ZhKxFFZOKedRwohlibVSXNkRS7Ae/YUxI1YOa57IUVzEcsWKxHgpYhWwbqRGJhFrUaxWtlwhlgkLvYsv0CFWiVhbs6UdscrEagymrRDLgqXHaSvEKhmrnjBiWXEeYiEWYi2AWBUgVgWIVQFiVYBYFSBWBYhVAWJVgFgVINb/dO0fEAAAAAAAAAD8YQ8OBAAAAACA/F8bQVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVaQ8OSAAAAAAE/X/dj1ABAAAAAAAAAAAAAACAlQB0Y5H7ngzE2AAAAABJRU5ErkJggg=="
      }
    }
  }
};
const PostCard = ({
  url,
  image = defaultImage,
  title,
  excerpt = null,
  size = "lg",
  readTime = 5,
  alignment = "vertical",
  loading = "lazy",
  className,
  // PostCard-specific props stripped before spreading onto <div>
  id: _id,
  category: _category,
  forceShowCategory: _forceShowCategory,
  forceHideReadTime: _forceHideReadTime,
  children: _children,
  ...rest
}) => {
  var _a;
  const variant = ((_a = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _a[size]) ?? {};
  const headingTextStyle = variant.headingTextStyle;
  const headingCss = variant.headingCss;
  const excerptTextStyle = variant.excerptTextStyle;
  const excerptColor = variant.excerptColor;
  const hasBoxShadow = size !== "xl";
  const cardImage = getPostCardImage({
    size,
    alignment,
    image
  });
  const cardImageRadius = getPostCardImageRadius({
    size,
    alignment
  });
  const containerClass = cx(variant.containerClass ?? fallbackContainerClass, hasBoxShadow ? withShadowClass : noShadowClass, className);
  const bodyClass = variant.bodyClass ?? fallbackBodyClass;
  const imageClass = variant.imageClass;
  return /* @__PURE__ */ jsxs("div", { "data-testid": "post-card-container", className: containerClass, ...rest, children: [
    url && size !== "xl" && /* @__PURE__ */ jsx("a", { className: overlayLinkStyles, ...url }),
    (cardImage == null ? void 0 : cardImage.sourceUrl) && /* @__PURE__ */ jsx("div", { className: imageClass, children: /* @__PURE__ */ jsxs("picture", { className: pictureStyles, children: [
      (cardImage == null ? void 0 : cardImage.sizes) && Object.keys(cardImage == null ? void 0 : cardImage.sizes).map((breakpoint) => {
        var _a2, _b, _c, _d, _e, _f;
        return /* @__PURE__ */ jsx(SourcePreload, { media: breakpoint, srcSet: (_b = (_a2 = cardImage == null ? void 0 : cardImage.sizes) == null ? void 0 : _a2[breakpoint]) == null ? void 0 : _b.sourceUrl, width: (_d = (_c = cardImage == null ? void 0 : cardImage.sizes) == null ? void 0 : _c[breakpoint]) == null ? void 0 : _d.width, height: (_f = (_e = cardImage == null ? void 0 : cardImage.sizes) == null ? void 0 : _e[breakpoint]) == null ? void 0 : _f.height, loading, decoding: loading === "lazy" ? "async" : void 0 }, breakpoint);
      }),
      /* @__PURE__ */ jsx(PreloadImg, { className: `${imgBaseStyles}${cardImageRadius ? ` ${imgRadiusStyles}` : ""}`, style: {
        width: "100%",
        height: "100%",
        objectFit: "cover"
      }, src: cardImage.sourceUrl, alt: cardImage.alt || title, width: cardImage.width, height: cardImage.height, loading, decoding: loading === "lazy" ? "async" : void 0 })
    ] }) }),
    /* @__PURE__ */ jsxs("div", { className: bodyClass, children: [
      /* @__PURE__ */ jsx("div", { className: badgeLabelStyles, children: /* @__PURE__ */ jsx(PostReadTime, { readTime }) }),
      size !== "xl" ? /* @__PURE__ */ jsx(
        Text,
        {
          textStyle: headingTextStyle,
          asChild: true,
          css: {
            color: "text.neutral",
            ...headingCss
          },
          children: /* @__PURE__ */ jsx("h2", { dangerouslySetInnerHTML: {
            __html: title
          } })
        }
      ) : /* @__PURE__ */ jsx("h2", { className: css({
        textStyle: headingTextStyle,
        ...headingCss ?? {}
      }), children: /* @__PURE__ */ jsx("a", { className: css({
        textDecoration: "none",
        color: "text.neutral !important",
        _hover: {
          color: "text.neutral !important"
        },
        _visited: {
          color: "text.neutral !important"
        }
      }), ...url, dangerouslySetInnerHTML: {
        __html: title
      } }) }),
      excerpt && /* @__PURE__ */ jsx(
        Text,
        {
          textStyle: excerptTextStyle ?? "body",
          asChild: true,
          css: {
            marginTop: "default",
            color: excerptColor ?? "text.subtle"
          },
          children: /* @__PURE__ */ jsx("p", { dangerouslySetInnerHTML: {
            __html: excerpt
          } })
        }
      ),
      size === "xl" && url && /* @__PURE__ */ jsx(Button, { size: "md", tone: "brand", emphasis: "filled", css: {
        marginTop: "loose"
      }, asChild: true, children: /* @__PURE__ */ jsx("a", { ...url, children: "Read article" }) })
    ] })
  ] });
};
export {
  PostCard as default
};
